import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Loader2, TestTube } from 'lucide-react';
import { apiService } from '../../services/api';

interface Props {
  apiType: 'cps' | 'chronogolf';
  apiConfig: { [key: string]: any };
  onValidationResult?: (result: { valid: boolean; message: string }) => void;
}

function ApiConfigValidator({ apiType, apiConfig, onValidationResult }: Props) {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    message: string;
    tested?: boolean;
  } | null>(null);

  const validateConfig = async () => {
    setIsValidating(true);
    setValidationResult(null);

    try {
      // Check if required fields are filled
      const requiredFields = getRequiredFields();
      const missingFields = requiredFields.filter(field => !apiConfig[field] || apiConfig[field].trim() === '');
      
      if (missingFields.length > 0) {
        const result = {
          valid: false,
          message: `Missing required fields: ${missingFields.join(', ')}`
        };
        setValidationResult(result);
        onValidationResult?.(result);
        return;
      }

      // Call backend validation API
      const result = await apiService.validateGolfCourseConfig({
        api_type: apiType,
        api_config: apiConfig
      });

      const validationResponse = {
        valid: result.valid,
        message: result.message,
        tested: true
      };

      setValidationResult(validationResponse);
      onValidationResult?.(result);

    } catch (error: any) {
      console.error('Validation error:', error);
      const result = {
        valid: false,
        message: error.message || 'Validation failed'
      };
      setValidationResult(result);
      onValidationResult?.(result);
    } finally {
      setIsValidating(false);
    }
  };

  const getRequiredFields = (): string[] => {
    if (apiType === 'cps') {
      return ['baseUrl', 'siteId', 'websiteId', 'referer'];
    } else {
      return ['baseUrl', 'clubUrl'];
    }
  };

  const isConfigComplete = () => {
    const requiredFields = getRequiredFields();
    return requiredFields.every(field => apiConfig[field] && apiConfig[field].trim() !== '');
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-900">API Configuration Validation</h4>
        
        <button
          onClick={validateConfig}
          disabled={isValidating || !isConfigComplete()}
          className={`flex items-center space-x-2 px-3 py-1 text-sm rounded-md transition-colors ${
            isValidating || !isConfigComplete()
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
        >
          {isValidating ? (
            <Loader2 className="animate-spin" size={14} />
          ) : (
            <TestTube size={14} />
          )}
          <span>{isValidating ? 'Testing...' : 'Test Configuration'}</span>
        </button>
      </div>

      {/* Configuration Status */}
      <div className="space-y-2">
        {/* Required Fields Check */}
        <div className="text-sm">
          <span className="text-gray-600">Required fields: </span>
          {isConfigComplete() ? (
            <span className="text-green-600 font-medium">✓ Complete</span>
          ) : (
            <span className="text-orange-600 font-medium">Incomplete</span>
          )}
        </div>

        {/* Validation Result */}
        {validationResult && (
          <div className={`flex items-start space-x-3 p-3 rounded-md ${
            validationResult.valid 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            {validationResult.valid ? (
              <CheckCircle className="text-green-500 mt-0.5" size={16} />
            ) : (
              <AlertCircle className="text-red-500 mt-0.5" size={16} />
            )}
            
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                validationResult.valid ? 'text-green-800' : 'text-red-800'
              }`}>
                {validationResult.valid ? 'Configuration Valid' : 'Configuration Invalid'}
              </p>
              <p className={`text-sm ${
                validationResult.valid ? 'text-green-700' : 'text-red-700'
              }`}>
                {validationResult.message}
              </p>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="text-xs text-gray-600 space-y-1">
          <p>• Fill in all required fields to enable testing</p>
          <p>• Test validates API connectivity and authentication</p>
          {apiType === 'cps' && (
            <p>• Ensure the Base URL and Referer URL match the golf course system</p>
          )}
          {apiType === 'chronogolf' && (
            <p>• Use the full ChronoGolf club booking URL (e.g., .../club/course-name)</p>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isValidating && (
        <div className="mt-3 flex items-center justify-center py-2">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Loader2 className="animate-spin" size={16} />
            <span>Testing API configuration...</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApiConfigValidator;